import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { LayoutGrid, Search, X } from "lucide-react";
import { useDocs } from "@/lib/DocsContext";
import { toTestId } from "@/lib/docs";

const DOT = { delivered: "bg-ok", action_required: "bg-warn", open_questions: "bg-primary", deferred: "bg-muted-foreground" };

const matchKind = (doc, text, q) => {
  if (!q) return "all";
  if ([doc.title, doc.path, doc.summary].some((s) => s.toLowerCase().includes(q))) return "meta";
  if (text && text.toLowerCase().includes(q)) return "content";
  return null;
};

const DocLink = ({ doc, hit, onNavigate }) => (
  <NavLink
    to={`/doc/${doc.path}`}
    onClick={onNavigate}
    data-testid={`sidebar-doc-link-${toTestId(doc.path)}`}
    className={({ isActive }) =>
      `group flex items-start gap-2.5 rounded-md border-l-2 px-3 py-1.5 text-[13px] leading-snug transition-colors ${
        isActive
          ? "border-primary bg-primary/10 font-medium text-foreground"
          : "border-transparent text-muted-foreground hover:bg-elevated hover:text-foreground"
      }`
    }
  >
    <span className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${DOT[doc.status] || DOT.delivered}`} />
    <span className="min-w-0">
      <span className="block truncate">{doc.title}</span>
      {hit === "content" && <span className="block font-mono text-[10px] text-warn">pogodak u sadržaju</span>}
    </span>
  </NavLink>
);

export const DocSidebar = ({ onNavigate }) => {
  const { manifest, texts } = useDocs();
  const [q, setQ] = useState("");
  const { pathname } = useLocation();
  const query = q.trim().toLowerCase();

  if (!manifest) return <div className="p-4 text-sm text-muted-foreground">Učitavanje…</div>;

  const groups = manifest.groups
    .map((g) => ({ ...g, hits: g.docs.map((d) => [d, matchKind(d, texts[d.path], query)]).filter(([, k]) => k) }))
    .filter((g) => g.hits.length);

  return (
    <nav className="flex h-full flex-col" data-testid="doc-sidebar">
      <div className="border-b border-border p-4">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Traži po nazivu ili sadržaju…"
            data-testid="sidebar-search-input"
            className="h-9 w-full rounded-md border border-border bg-background pl-9 pr-8 text-sm outline-none transition-colors placeholder:text-muted-foreground focus:border-primary"
          />
          {q && (
            <button type="button" onClick={() => setQ("")} data-testid="sidebar-search-clear-btn" aria-label="Očisti" className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
      <div className="flex-1 space-y-5 overflow-y-auto p-3">
        <Link
          to="/documentation"
          onClick={onNavigate}
          data-testid="sidebar-overview-link"
          className={`flex items-center gap-2 rounded-md px-3 py-2 text-[13px] font-semibold transition-colors ${pathname === "/" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-elevated hover:text-foreground"}`}
        >
          <LayoutGrid className="h-4 w-4" /> Pregled i checklista
        </Link>
        {groups.map((g, i) => (
          <section key={g.id} data-testid={`sidebar-group-${g.id}`}>
            <h3 className="mb-1.5 flex items-center justify-between px-3 font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              <span>{String(manifest.groups.indexOf(manifest.groups.find((x) => x.id === g.id)) + 1).padStart(2, "0")} · {g.title}</span>
              <span>{g.hits.length}</span>
            </h3>
            <div className="space-y-0.5">
              {g.hits.map(([d, hit]) => (
                <DocLink key={d.path} doc={d} hit={hit} onNavigate={onNavigate} />
              ))}
            </div>
          </section>
        ))}
        {!groups.length && (
          <p className="px-3 text-sm text-muted-foreground" data-testid="sidebar-no-results">
            Nema rezultata za „{q}”.
          </p>
        )}
      </div>
      <div className="border-t border-border px-4 py-3 font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
        Statični preglednik · samo čitanje
      </div>
    </nav>
  );
};
